import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Battery, BellRing, Check, CheckCircle2, ChevronsLeft, ChevronsRight, Clock, Copy, FileText, Lock, Moon, PhoneIncoming, Signal, Siren, Smartphone, Sparkles, Sun, Wifi } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { detectOS } from '../../data/mobileForwarding';
import { getDemoDevice, setDemoDevice } from '../../utils/demoDevice';
type ConsoleTone = 'vexu' | 'red' | 'blue' | 'amber' | 'neutral';
interface MobileFrameProps {
  children: React.ReactNode;
  className?: string;
  onSimulateCall?: () => void;
  onSimulateEmergencyCall?: () => void;
  onSimulateFree?: () => void;
  onSimulateTrial?: () => void;
  onSimulateConsentWithTrial?: () => void;
  onSimulateConsentWithoutTrial?: () => void;
  onSimulateActive?: () => void;
  onSimulateOverage?: () => void;
  onToggleVexuNotification?: () => void;
  vexuNotificationActive?: boolean;
  /** Human-readable state of the sticky Vexu notification, e.g. "Active". */
  vexuNotificationStatus?: string | null;
}
type PhoneTypeId = 'iphone' | 'samsung' | 'android';
const PHONE_TYPES: {
  id: PhoneTypeId;
  label: string;
}[] = [{
  id: 'iphone',
  label: 'iPhone'
}, {
  id: 'samsung',
  label: 'Samsung'
}, {
  id: 'android',
  label: 'Android'
}];
const PHONE_WIDTH = 390;
const PHONE_HEIGHT = 844;
const TEST_AGENT_NUMBER = '+1 (765) 632-2092';
function toneClasses(tone: ConsoleTone, isLight: boolean) {
  switch (tone) {
    case 'vexu':
      return isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu';
    case 'red':
      return isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/15 text-red-400';
    case 'blue':
      return isLight ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/15 text-blue-400';
    case 'amber':
      return isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/15 text-amber-400';
    default:
      return isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/[0.07] text-zinc-400';
  }
}
function SectionLabel({
  label,
  isLight
}: {
  label: string;
  isLight: boolean;
}) {
  return <span className={`block text-[8.5px] font-bold uppercase tracking-[0.14em] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-796" data-mp="3ov1mw97qp0yc">
      {label}
    </span>;
}
interface ConsoleRowProps {
  isLight: boolean;
  icon: React.ElementType;
  tone: ConsoleTone;
  title: string;
  detail: string;
  badge?: string;
  badgeActive?: boolean;
  onClick: () => void;
}
function ConsoleRow({
  isLight,
  icon: Icon,
  tone,
  title,
  detail,
  badge,
  badgeActive,
  onClick
}: ConsoleRowProps) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-2 rounded-xl border px-2 py-1.5 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06]'}`} data-id="element-797" data-mp="qb1qbytgil93">
      <span className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg ${toneClasses(tone, isLight)}`} data-id="element-798" data-mp="2n5y3ljp0nwnb">
        <Icon className="h-3 w-3" data-id="element-799" data-mp="12ooxg143l35z" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-800" data-mp="17lyil4kgeso0">
        <span className="flex items-center gap-1.5" data-id="element-801" data-mp="17kx5yvnshy46">
          <span className={`truncate text-[11.5px] font-semibold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-802" data-mp="sxc2v1qwzyw9">
            {title}
          </span>
          {badge && <span className={`flex-shrink-0 rounded px-1 py-[1px] text-[8px] font-bold uppercase tracking-wider ${badgeActive ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/[0.07] text-zinc-500'}`} data-id="element-803" data-mp="jv2w8myropo5">
              {badge}
            </span>}
        </span>
        <span className={`mt-0.5 block truncate text-[9.5px] leading-tight ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-804" data-mp="24by19rubj7kb">
          {detail}
        </span>
      </span>
    </button>;
}
interface ConsoleChipProps {
  isLight: boolean;
  icon: React.ElementType;
  tone: ConsoleTone;
  label: string;
  onClick: () => void;
}
function ConsoleChip({
  isLight,
  icon: Icon,
  tone,
  label,
  onClick
}: ConsoleChipProps) {
  return <button type="button" onClick={onClick} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06]'}`} data-id="element-805" data-mp="6jpy4z1h9eb">
      <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-md ${toneClasses(tone, isLight)}`} data-id="element-806" data-mp="3hnt8a364denz">
        <Icon className="h-2.5 w-2.5" data-id="element-807" data-mp="3dkwwavb34gt0" />
      </span>
      <span className={`truncate text-[10.5px] font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-808" data-mp="38oppz1es13aj">
        {label}
      </span>
    </button>;
}
export function MobileFrame({
  children,
  className = '',
  onSimulateCall,
  onSimulateEmergencyCall,
  onSimulateFree,
  onSimulateTrial,
  onSimulateConsentWithTrial,
  onSimulateConsentWithoutTrial,
  onSimulateActive,
  onSimulateOverage,
  onToggleVexuNotification,
  vexuNotificationActive,
  vexuNotificationStatus
}: MobileFrameProps) {
  const {
    isLight,
    toggleTheme
  } = useTheme();
  const [scale, setScale] = useState(1);
  const [consoleOpen, setConsoleOpen] = useState(true);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const calculateScale = useCallback(() => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Reserve padding around the phone (48px top/bottom, console width right)
    const availableHeight = vh - 96;
    const availableWidth = vw - 300;
    const scaleByHeight = availableHeight / PHONE_HEIGHT;
    const scaleByWidth = availableWidth / PHONE_WIDTH;
    // Use the smaller scale, capped at 1 (never larger than native)
    const newScale = Math.min(scaleByHeight, scaleByWidth, 1);
    // Minimum scale to prevent it from getting too tiny
    setScale(Math.max(newScale, 0.45));
  }, []);
  useEffect(() => {
    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [calculateScale]);
  const [phoneType, setPhoneType] = useState<PhoneTypeId>(() => {
    const d = getDemoDevice() ?? {
      os: detectOS(),
      androidBrand: 'samsung' as const
    };
    return d.os === 'ios' ? 'iphone' : d.androidBrand === 'samsung' ? 'samsung' : 'android';
  });
  const choosePhoneType = (id: PhoneTypeId) => {
    setPhoneType(id);
    setDemoDevice(id === 'iphone' ? {
      os: 'ios',
      androidBrand: 'samsung'
    } : {
      os: 'android',
      androidBrand: id === 'samsung' ? 'samsung' : 'other'
    });
  };
  const copyTestNumber = () => {
    navigator.clipboard?.writeText(TEST_AGENT_NUMBER).catch(() => undefined);
    setCopiedNumber(true);
    window.setTimeout(() => setCopiedNumber(false), 1600);
  };
  return <div style={{
    height: '100dvh'
  }} className={`w-full flex items-center justify-center font-sans transition-colors duration-500 ${isLight ? 'bg-stone-100 text-slate-900' : 'bg-black text-white'}`} data-id="element-809" data-mp="2hic3qerqljx4">
      {/* Demo Console — preview any state of the app instantly */}
      <div style={{
      position: 'absolute',
      right: 'clamp(12px, 3vw, 32px)',
      top: '50%',
      transform: 'translateY(-50%)'
    }} className="z-50" data-id="element-810" data-mp="2g39cre9kr6gj">
        {consoleOpen ? <div className={`flex max-h-[92dvh] w-[232px] flex-col overflow-hidden rounded-2xl shadow-xl ${isLight ? 'bg-white ring-1 ring-slate-200 shadow-slate-900/[0.06]' : 'bg-zinc-900 ring-1 ring-white/10 shadow-black/40'}`} data-id="element-811" data-mp="3crfqqrsdzb7h">
            {/* Header */}
            <div className="flex-shrink-0 px-3.5 pb-3 pt-3.5" data-id="element-812" data-mp="32zjfsg57bqlu">
              <div className="flex items-center justify-between gap-2" data-id="element-813" data-mp="3qqow0wf20kwo">
                <div className="flex min-w-0 items-center gap-1.5" data-id="element-814" data-mp="3viglt4qdymlr">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-vexu" data-id="element-815" data-mp="36rkdrs9bwok0" />
                  <span className={`truncate text-[12.5px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-816" data-mp="36tnbkygq3lza">
                    Demo Console
                  </span>
                </div>
                <button onClick={() => setConsoleOpen(false)} aria-label="Collapse demo console" className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600' : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-300'}`} data-id="element-817" data-mp="2s36dnp3are75">
                  <ChevronsRight className="h-3.5 w-3.5" data-id="element-818" data-mp="3ated9x7sfmil" />
                </button>
              </div>
              <p className={`mt-1 text-[10px] leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-819" data-mp="24ctz3vdjqj7l">
                Preview any state instantly — nothing here affects a real
                account.
              </p>
            </div>

            <div className={`flex-1 overflow-y-auto border-t px-3 py-3 ${isLight ? 'border-slate-100' : 'border-white/[0.06]'}`} data-id="element-820" data-mp="eu3rog68e195">
              {/* Live events */}
              <SectionLabel isLight={isLight} label="Live events" data-id="element-821" data-mp="os1ecz8fzpzn" />
              <div className="mt-1.5 space-y-1.5" data-id="element-822" data-mp="tlmh14kzd856">
                {onSimulateCall && <ConsoleRow isLight={isLight} icon={PhoneIncoming} tone="vexu" title="Incoming call" detail="Shows for 6s · tap again to hide" onClick={onSimulateCall} data-id="element-823" data-mp="yiw3cbd2p3os" />}
                {onSimulateEmergencyCall && <ConsoleRow isLight={isLight} icon={Siren} tone="red" title="Emergency call" detail="Shows for 6s · tap alert to listen live" onClick={onSimulateEmergencyCall} data-id="element-824" data-mp="39cievf76uksy" />}
                {onToggleVexuNotification && <ConsoleRow isLight={isLight} icon={BellRing} tone={vexuNotificationActive ? 'vexu' : 'neutral'} title="Notification bar" detail="Shows for 6s · tap again to hide" badge={vexuNotificationStatus ?? 'Off'} badgeActive={Boolean(vexuNotificationActive)} onClick={onToggleVexuNotification} data-id="element-825" data-mp="2omlmrjc4v79" />}
              </div>

              {/* Account state */}
              {(onSimulateFree || onSimulateTrial || onSimulateActive || onSimulateOverage) && <>
                  <div className="mt-4" data-id="element-826" data-mp="ze3ybqgqjhig">
                    <SectionLabel isLight={isLight} label="Account state" data-id="element-827" data-mp="zf5bc0hbae2t" />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-828" data-mp="2jy7ox0ko3vt7">
                    {onSimulateFree && <ConsoleChip isLight={isLight} icon={Lock} tone="neutral" label="Free plan" onClick={onSimulateFree} data-id="element-829" data-mp="2ezxu4r2uux84" />}
                    {onSimulateTrial && <ConsoleChip isLight={isLight} icon={Clock} tone="blue" label="Free trial" onClick={onSimulateTrial} data-id="element-830" data-mp="2ym2faab7do34" />}
                    {onSimulateActive && <ConsoleChip isLight={isLight} icon={CheckCircle2} tone="vexu" label="Paid & active" onClick={onSimulateActive} data-id="element-831" data-mp="3mc6n6tvfc9zx" />}
                    {onSimulateOverage && <ConsoleChip isLight={isLight} icon={AlertTriangle} tone="amber" label="Over limit" onClick={onSimulateOverage} data-id="element-832" data-mp="9cgom5vxcmpj" />}
                  </div>
                </>}

              {/* Consent */}
              {(onSimulateConsentWithTrial || onSimulateConsentWithoutTrial) && <>
                  <div className="mt-4" data-id="element-833" data-mp="3crcnc995r65m">
                    <SectionLabel isLight={isLight} label="Consent screen" data-id="element-834" data-mp="3cs45oidqv7dy" />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-835" data-mp="16zpcu8nb89v8">
                    {onSimulateConsentWithTrial && <ConsoleChip isLight={isLight} icon={Sparkles} tone="blue" label="With trial" onClick={onSimulateConsentWithTrial} data-id="element-836" data-mp="1az6hizuwx2zc" />}
                    {onSimulateConsentWithoutTrial && <ConsoleChip isLight={isLight} icon={FileText} tone="neutral" label="No trial" onClick={onSimulateConsentWithoutTrial} data-id="element-837" data-mp="30guz34gjytw0" />}
                  </div>
                </>}

              {/* Phone type — previews iPhone / Android setup steps */}
              <div className="mt-4" data-id="element-838" data-mp="3j8e23baku7y1">
                <SectionLabel isLight={isLight} label="Phone type" data-id="element-839" data-mp="1tsenvfs3psbj" />
              </div>
              <div role="radiogroup" aria-label="Phone type" className={`mt-1.5 grid grid-cols-3 gap-1 rounded-xl p-1 ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-840" data-mp="1otl9zm8wjzau">
                {PHONE_TYPES.map(p => {
              const selected = phoneType === p.id;
              return <button key={p.id} type="button" role="radio" aria-checked={selected} onClick={() => choosePhoneType(p.id)} className={`flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${selected ? isLight ? 'bg-white text-slate-900 shadow-sm' : 'bg-zinc-800 text-white shadow-sm' : isLight ? 'text-slate-500 hover:text-slate-700' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-841" data-mp="209g7ow8dnkat">
                      <Smartphone className="h-3 w-3" data-id="element-842" data-mp="osq4llz938lf" />
                      {p.label}
                    </button>;
            })}
              </div>

              {/* Appearance */}
              <div className="mt-4" data-id="element-843" data-mp="m80n09s0rfwn">
                <SectionLabel isLight={isLight} label="Appearance" data-id="element-844" data-mp="m6z7nt3z0794" />
              </div>
              <div className={`mt-1.5 grid grid-cols-2 gap-1 rounded-xl p-1 ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-845" data-mp="h85trzkrue8f">
                <button onClick={() => !isLight && toggleTheme()} className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${isLight ? 'bg-white text-slate-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-846" data-mp="7b16fwf6wcq1">
                  <Sun className="h-3 w-3" data-id="element-847" data-mp="3pqk6onwmtdfu" />
                  Light
                </button>
                <button onClick={() => isLight && toggleTheme()} className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${!isLight ? 'bg-zinc-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-848" data-mp="3ksil9wbqh217">
                  <Moon className="h-3 w-3" data-id="element-849" data-mp="317ie9qensqfr" />
                  Dark
                </button>
              </div>
            </div>

            {/* Test agent footer */}
            <div className={`flex-shrink-0 border-t px-3.5 py-3 ${isLight ? 'border-slate-100 bg-slate-50/60' : 'border-white/[0.06] bg-white/[0.02]'}`} data-id="element-850" data-mp="2s5sk4cte7dea">
              <div className="flex items-center justify-between gap-2" data-id="element-851" data-mp="3ku7ng235w79s">
                <div className="min-w-0" data-id="element-852" data-mp="3oui4qawemk3y">
                  <div className="flex items-center gap-1.5" data-id="element-853" data-mp="3ou8dyl3jc8x7">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-vexu" data-id="element-854" data-mp="3b3v7yoa7f4ai" />
                    <span className={`text-[8.5px] font-bold uppercase tracking-[0.12em] ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-855" data-mp="3g26fgsd112pk">
                      Live test agent
                    </span>
                  </div>
                  <span className={`mt-0.5 block font-mono text-[12px] font-bold leading-none ${isLight ? 'text-slate-800' : 'text-white'}`} data-id="element-856" data-mp="ceo2dqcn9v5t">
                    {TEST_AGENT_NUMBER}
                  </span>
                </div>
                <button onClick={copyTestNumber} aria-label="Copy test agent number" className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 ring-1 ring-slate-200 hover:bg-white hover:text-slate-700' : 'text-zinc-500 ring-1 ring-white/10 hover:bg-white/5 hover:text-zinc-200'}`} data-id="element-857" data-mp="vgwuff0ju3ss">
                  {copiedNumber ? <Check className="h-3.5 w-3.5 text-vexu" data-id="element-858" data-mp="cp43dq51isnp" /> : <Copy className="h-3.5 w-3.5" data-id="element-859" data-mp="7ugnugs0l8l9" />}
                </button>
              </div>
              <p className={`mt-1.5 text-[9.5px] leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-860" data-mp="1j7j2h6ajzo2s">
                Call it from any phone to hear the real Vexu agent answer.
              </p>
            </div>
          </div> : <button onClick={() => setConsoleOpen(true)} aria-label="Open demo console" className={`flex items-center gap-1.5 rounded-full px-3 py-2 shadow-lg transition-all ${isLight ? 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50' : 'bg-zinc-900 text-zinc-300 ring-1 ring-white/10 hover:bg-zinc-800'}`} data-id="element-861" data-mp="3hn9u0fxwnhyv">
            <ChevronsLeft className="h-3.5 w-3.5" data-id="element-862" data-mp="56mlavj3m5j4" />
            <span className="text-[11px] font-semibold" data-id="element-863" data-mp="99j8z0nhwrtx">Demo</span>
          </button>}
      </div>

      {/* Scaled Phone Container */}
      <div style={{
      width: PHONE_WIDTH,
      height: PHONE_HEIGHT,
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      flexShrink: 0
    }} data-id="element-864" data-mp="e7chi0c9txxd">
        {/* Phone Frame — outer shell */}
        <div className={`relative w-full h-full shadow-2xl rounded-[40px] border-[8px] transition-colors duration-500 ${isLight ? 'bg-[#e8e8e6] border-[#e8e8e6] ring-1 ring-black/[0.04] shadow-black/[0.08]' : 'bg-zinc-900 border-zinc-900 ring-1 ring-white/10'}`} data-id="element-865" data-mp="28g7w57i58cdr">
          {/* Inner Screen Mask — clips all content to the inner rounded area */}
          <div className="absolute inset-0 rounded-[32px] overflow-hidden" data-id="element-866" data-mp="1tpih1u1kad4m">
            {/* Status Bar */}
            <div className={`absolute top-0 left-0 right-0 h-12 px-6 flex items-center justify-between z-50 transition-colors duration-500 ${isLight ? 'text-slate-800 bg-white/60 backdrop-blur-2xl' : 'text-white bg-dark/80 backdrop-blur-2xl border-b border-white/5'}`} data-id="element-867" data-mp="2rczmg0w3rrrb">
              <div className="text-sm font-semibold ml-2" data-id="element-868" data-mp="2dhx7ko9a2s2d">9:41</div>
              <div className="flex items-center gap-1.5" data-id="element-869" data-mp="162sk3742l8qv">
                <Signal className="w-4 h-4" data-id="element-870" data-mp="120ns0ct9sk6c" />
                <Wifi className="w-4 h-4" data-id="element-871" data-mp="11yuj0qv6pnv6" />
                <Battery className="w-5 h-5" data-id="element-872" data-mp="2bnowxtpvmr7" />
              </div>
            </div>

            {/* Dynamic Island Placeholder */}
            <div className={`absolute top-2 left-1/2 -translate-x-1/2 w-[120px] h-[35px] rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-black ring-1 ring-black/5' : 'bg-black ring-1 ring-white/5'}`} data-id="element-873" data-mp="3tpk0a83a1pdz" />

            {/* Content Area */}
            <div className={`w-full h-full overflow-y-auto pt-12 pb-safe transition-colors duration-500 ${isLight ? 'app-gradient-bg-light' : 'app-gradient-bg'} ${className}`} data-id="element-874" data-mp="l9qilpx7m7vc">
              {children}
            </div>

            {/* Home Indicator */}
            <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-slate-900/20' : 'bg-white/20'}`} data-id="element-875" data-mp="7bjuh1i2bm8v" />
          </div>
        </div>
      </div>
    </div>;
}